import axios from 'axios';
import { Moment } from 'moment';
import { DossierId } from '@/dossier/models/AbstractDossier';
import MediaData from '@/media/models/MediaData';
import MediaTargetType from '@/shared/constants/MediaTargetType';
import { DateTimeFormat, formatMoment } from '@/shared/converters/dateConverters';
import { ObjectIdentifier } from '@/vehicle/models/Vehicle';
import { formatImageData } from '../converters/imageDataConverters';
import { parseMediaFiles } from '../converters/mediaFileConverters';
import { ImageData } from '../models/ImageData';
import MediaContext from '../models/MediaContext';
import MediaFile, { ContentId } from '../models/MediaFile';

export default class MediaFileClient {
  static async allByDossier(dossierId: DossierId): Promise<Array<MediaFile>> {
    const response = await axios.get(`/api/mobopro/v1/dossiers/${dossierId}/media`);
    return parseMediaFiles(response.data);
  }

  static async edit(mediaFiles: Array<MediaFile>, isLocked?: boolean, hasWarranty?: boolean): Promise<void> {
    await axios.patch('/api/mobopro/v1/mediafiles', mediaFiles.map(mediaFile => {
      return {
        id: mediaFile.contentId,
        externalEntityType: mediaFile.target,
        isLocked,
        isWarranty: hasWarranty,
      };
    }));
  }

  static async remove(mediaFiles: Array<MediaFile>): Promise<void> {
    const promises = mediaFiles.map(file => {
      return axios.delete(`/api/mobopro/v1/mediafiles?mediafileid=${file.contentId}&externalentitytype=${file.target}`);
    });

    await Promise.all(promises);
  }

  static async createMedia(attachmentData: MediaData, context: MediaContext): Promise<void> {
    const formData = new FormData();
    attachmentData.files.forEach(f => formData.append('Files', f));
    formData.append('Description', attachmentData.description);
    formData.append('AttachmentDate', formatMoment(attachmentData.date, DateTimeFormat.DATE));
    formData.append('IsLocked', `${!attachmentData.sharedWithClient}`);
    formData.append('IsWarranty', `${attachmentData.isWarranty}`);

    const path = context.visit<string>({
      dossier: c => `/api/mobopro/v1/dossiers/${c.dossierId}/attachments`,
      job: c => `/api/mobopro/v1/dossiers/${context.dossierId}/jobs/${c.job.id}/medias`,
      diagnostic: c => `/api/mobopro/v1/dossiers/${context.dossierId}/jobs/${c.job.id}/diagnostics/${c.diagnostic.id}/medias`,
    });

    await axios.post(path, formData);
  }

  static async updateNote(id: ContentId, target: MediaTargetType, note: string): Promise<void> {
    await axios.patch('/api/mobopro/v1/mediafiles', [{
      id,
      externalEntityType: target,
      description: note || null,
    }]);
  }
}
